// File: frontend/src/hooks/useNewsletter.js

import { useState, useCallback } from 'react';
import { API_ENDPOINTS } from '../config/constants';
import apiService from '../services/api';

export const useNewsletter = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);

  const clearStates = () => {
    setError(null);
    setSuccess(false);
    setSubscriptionData(null);
  };

  const mapErrorToCode = (error) => {
    if (!error) return 'unknown_error';
    
    // If the error has a response with a code
    if (error.response && error.response.data && error.response.data.code) {
      switch (error.response.data.code) {
        case 'INVALID_EMAIL':
          return 'invalid_email';
        case 'ALREADY_SUBSCRIBED':
          return 'already_subscribed';
        case 'NOT_FOUND':
          return 'not_found';
        case 'INVALID_TOKEN':
          return 'invalid_token';
        default:
          return error.response.data.code.toLowerCase();
      }
    }
    
    // If the error has a code directly
    if (error.code) {
      switch (error.code) {
        case 'VALIDATION_ERROR':
          return 'invalid_email';
        case 'CONFLICT':
          return 'already_subscribed';
        case 'NETWORK_ERROR':
          return 'connection_error';
        default:
          return error.code.toLowerCase();
      }
    }
    
    // Default error
    return 'subscription_failed';
  };

  const handleError = (error) => {
    const errorCode = mapErrorToCode(error);
    setError(errorCode);
  };

  // Memoize functions with useCallback to prevent recreation on each render
  const subscribe = useCallback(async (formData) => {
    setIsLoading(true);
    clearStates();

    try {
      const { emailAddress, firstName, lastName } = formData;
      
      const response = await apiService.post(API_ENDPOINTS.ENGAGEMENT.SUBSCRIBE, {
        emailAddress,
        firstName,
        lastName,
        tags: {
          source: 'website',
          platform: 'web',
          timestamp: new Date().toISOString()
        }
      });

      setSuccess(true);
      setSubscriptionData(response.data);
      return response.data;
    } catch (err) {
      handleError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const unsubscribe = useCallback(async (data) => {
    setIsLoading(true);
    clearStates();

    try {
      const { emailAddress, reason } = data;
      
      const response = await apiService.post(API_ENDPOINTS.ENGAGEMENT.UNSUBSCRIBE, {
        emailAddress,
        reason
      });

      setSuccess(true);
      return response.data;
    } catch (err) {
      handleError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const verifyPreferencesToken = useCallback(async (token, emailAddress) => {
    setIsLoading(true);
    clearStates();
    
    try {
      const response = await apiService.post(API_ENDPOINTS.ENGAGEMENT.VERIFY_PREFERENCES_TOKEN, {
        token,
        emailAddress
      });
      
      return response.data;
    } catch (err) {
      handleError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);
  const clearSuccess = useCallback(() => {
    setSuccess(false);
    setSubscriptionData(null);
  }, []);

  return {
    subscribe,
    unsubscribe,
    verifyPreferencesToken,
    isLoading,
    error,
    success,
    subscriptionData,
    clearError,
    clearSuccess
  };
};